// =============================================================================
// features/meu-whatsapp/SyncPanel.tsx — Resultado da importação, para o
// vendedor (F39-S10).
//
// Escrito para quem conectou o próprio celular, não para o administrador:
// "clientes que já estavam no sistema" em vez de "leads vinculados",
// "conversas ignoradas" em vez de "chats descartados por allowlist".
//
// O intervalo mostrado é o REAL entregue pelo WhatsApp, nunca "30 dias" fixo.
// =============================================================================

import * as React from 'react';

import { cn } from '../../lib/cn';

import type { SyncResult } from './useMeuWhatsApp';

function formatDate(iso: string | null): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function Metric({ value, label }: { value: number; label: string }): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <span
        className="font-display font-bold text-ink leading-none"
        style={{ fontSize: 'var(--text-2xl)' }}
      >
        {value}
      </span>
      <span className="font-sans text-xs text-ink-3 mt-1">{label}</span>
    </div>
  );
}

export function SyncPanel({ sync }: { sync: SyncResult | null }): React.JSX.Element {
  if (sync === null) {
    return (
      <p className="font-sans text-sm text-ink-2">
        Vamos trazer seus contatos em instantes. Você não precisa fazer nada.
      </p>
    );
  }

  if (sync.status === 'pending' || sync.status === 'running') {
    return (
      <div className="flex items-center gap-3">
        <div className="w-4 h-4 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
        <p className="font-sans text-sm text-ink-2">
          Trazendo suas conversas… isso pode levar alguns minutos. Pode fechar a página, continua
          rodando.
        </p>
      </div>
    );
  }

  if (sync.status === 'failed') {
    return (
      <p className="font-sans text-sm text-danger">
        Não conseguimos trazer suas conversas agora. Avise o administrador — seu WhatsApp continua
        conectado.
      </p>
    );
  }

  const inicio = formatDate(sync.oldest_chat_at);
  const fim = formatDate(sync.newest_chat_at);

  return (
    <div className="flex flex-col gap-4">
      <p className="font-sans text-sm text-ink">
        Pronto. Seus clientes já estão no sistema, vinculados a você.
      </p>

      <div className="grid grid-cols-3 gap-4">
        <Metric value={sync.leads_created} label="clientes novos" />
        <Metric value={sync.leads_linked} label="já estavam no sistema" />
        <Metric value={sync.contacts_in_window} label="conversas trazidas" />
      </div>

      <p className="font-sans text-sm text-ink-2">
        <strong className="font-semibold text-ink">{sync.messages_imported}</strong>{' '}
        {sync.messages_imported === 1 ? 'mensagem importada' : 'mensagens importadas'}. As conversas
        entraram no seu nome — a assistente virtual não responde por cima delas.
      </p>

      <div className="flex flex-col gap-1 border-t border-border pt-3">
        {inicio !== null && fim !== null ? (
          <p className="font-sans text-xs text-ink-3">
            Período trazido: <strong className="font-semibold text-ink-2">{inicio}</strong> a{' '}
            <strong className="font-semibold text-ink-2">{fim}</strong>
          </p>
        ) : null}

        {sync.chats_ignored > 0 ? (
          <p className="font-sans text-xs text-ink-3">
            {sync.chats_ignored}{' '}
            {sync.chats_ignored === 1 ? 'conversa ignorada' : 'conversas ignoradas'} — grupos,
            listas de transmissão e contatos sem conversa recente não entram.
          </p>
        ) : null}

        {sync.errors_count > 0 ? (
          <p className={cn('font-sans text-xs text-danger')}>
            {sync.errors_count} {sync.errors_count === 1 ? 'contato' : 'contatos'} não pôde ser
            trazido.
          </p>
        ) : null}
      </div>
    </div>
  );
}
